/* eslint-disable camelcase */
import { Loader, StyledReactSelect } from '@fountain/fountain-ui-components';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import CopyIcon from '@material-ui/icons/FileCopy';
import React, { useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';

import { languages } from '../constants';
import { messages } from '../messages';
import { AxAgentValues } from '../types';
import { extractDomain, isValidURL } from '../utils';
import { DeleteWebsiteConfirmationModal } from './modals/DeleteWebsiteConfirmationModal';
import { EmbedCodeModal } from './modals/EmbedCodeModal';
import { useAIAgentFunctionalitiesStyles } from './styles';

// ********************************************************************************
// == Type ========================================================================
export interface AIAgentFunctionalitiesProps {
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setAxAgentValues: React.Dispatch<React.SetStateAction<AxAgentValues>>;
  axAgentValues: AxAgentValues;
  errors: {
    [key: string]: string;
  };
  isLoading: boolean;
  isCustomWebsiteEnable: boolean;
  setIsCustomWebsiteEnable: React.Dispatch<React.SetStateAction<boolean>>;
}

type FallbackMethod = 'Email' | 'PhoneNumber' | 'Website';

// == Constant ====================================================================
const getFirstNonBlankMethod = (axAgentValues: AxAgentValues): FallbackMethod => {
  const defaultAnswer: FallbackMethod = 'Email';

  if (axAgentValues.emailFallback) {
    return defaultAnswer;
  }

  if (axAgentValues.phoneNumberFallback) {
    return 'PhoneNumber';
  }

  if (axAgentValues.websiteFallback) {
    return 'Website';
  }

  return defaultAnswer; 
}

// == Component ===================================================================
export const AIAgentFunctionalities: VFC<AIAgentFunctionalitiesProps> = ({
  handleChange,
  setAxAgentValues,
  axAgentValues,
  errors,
  isLoading,
  isCustomWebsiteEnable,
  setIsCustomWebsiteEnable,
}) => {
  const classes = useAIAgentFunctionalitiesStyles();
  const intl = useIntl();
  const dispatch = useDispatch();

  // -- State ---------------------------------------------------------------------
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [fallbackMethod, setFallbackMethod] = useState<FallbackMethod>(() => getFirstNonBlankMethod(axAgentValues));
  const [newWebsite, setNewWebsite] = useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openEmbedCodeModal, setOpenEmbedCodeModal] = useState(false);
  const [showAddWebsite, setShowAddWebsite] = useState(false);
  const { whats_app_enabled: whatsappEnabled } =
    useSelector(makeSelectWhoami());

  // -- Handler -------------------------------------------------------------------
  const handleAddWebsite = () => {
    if (newWebsite && isValidURL(newWebsite)) {
      const domain = extractDomain(newWebsite);
      setAxAgentValues(prev => {
        const updatedWebsites = [...(prev.customWebsites ?? [])];
        if (editingIndex !== null) {
          updatedWebsites[editingIndex] = domain;
        } else {
          updatedWebsites.push(domain);
        }
        return {
          ...prev,
          customWebsites: updatedWebsites,
        };
      });
      setNewWebsite('');
      setShowAddWebsite(false);
      setEditingIndex(null);
    } else {
      dispatch(
        addMessageAction(intl.formatMessage(messages.enterValidUrl), 'error'),
      );
    }
  };

  const handleDeleteWebsite = (index: number) => {
    setDeleteIndex(index);
    setOpenConfirmDialog(true);
  };

  const toggleAddWebsite = () => {
    setShowAddWebsite(!showAddWebsite);
    setNewWebsite('');
    setEditingIndex(null);
  };

  const handleCustomWebsiteToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked;
    setIsCustomWebsiteEnable(isChecked);
    setAxAgentValues(prev => ({
      ...prev,
      customWebsites: isChecked ? prev.customWebsites : [],
    }));
  };

  const handleFallbackMethodChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setFallbackMethod(event.target.value as FallbackMethod);
  };

  const handleCloseEmbedModal = () => {
    setOpenEmbedCodeModal(false);
  };

  const handleLanguageChange = (selectedLanguage: { key: string }) => {
    handleChange({
      target: {
        name: 'languageCode',
        type: 'text',
        value: selectedLanguage.key,
        checked: false,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  // -- UI ------------------------------------------------------------------------
  if (isLoading) return <Loader fullScreen size="2rem" />;
  return (
    <Grid className={classes.container}>
      <Grid className={classes.header}>
        <Typography variant="h2">
          <FormattedMessage {...messages.setAIAgentFunctionalities} />
        </Typography>
      </Grid>
      <Grid className={classes.content}>
        <Grid className={classes.sectionHeader}>
          <Typography variant="h3">
            <FormattedMessage {...messages.aiAgentVisibility} />
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <Typography variant="body2">
            <FormattedMessage {...messages.aiAgentVisibilityInstruction} />
          </Typography>
        </Grid>
        <Grid>
          <Grid className={classes.contactInfo}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={axAgentValues.enableInFountainPlatforms}
                  onChange={handleChange}
                  name="enableInFountainPlatforms"
                />
              }
              label={
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography variant="body1" className={classes.label}>
                    Fountain Website
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    <FormattedMessage
                      {...messages.fountainWebsiteDescription}
                    />
                  </Typography>
                </Box>
              }
              className={classes.visibilityLabel}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={axAgentValues.smsEnabled}
                  onChange={handleChange}
                  name="smsEnabled"
                />
              }
              label={
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  {whatsappEnabled ? (
                    <>
                      <Typography variant="body1" className={classes.label}>
                        SMS / WhatsApp
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        <FormattedMessage
                          {...messages.smsWhatsappEnabledDescription}
                        />
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body1" className={classes.label}>
                        SMS
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        <FormattedMessage {...messages.smsEnabledDescription} />
                      </Typography>
                    </>
                  )}
                </Box>
              }
              className={classes.visibilityLabel}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCustomWebsiteEnable}
                  onChange={handleCustomWebsiteToggle}
                  name="enableInFountainPlatforms"
                />
              }
              label={
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography variant="body1" className={classes.label}>
                    Custom Website
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    <FormattedMessage {...messages.customWebsiteDescription} />
                  </Typography>
                </Box>
              }
              className={classes.visibilityLabel}
            />
          </Grid>

          {isCustomWebsiteEnable && (
            <Grid item xs={12} className={classes.customWebsiteSection}>
              <Button
                onClick={() => {
                  setOpenEmbedCodeModal(true);
                }}
                startIcon={<CopyIcon />}
                variant="outlined"
                color="secondary"
              >
                Get embed code
              </Button>
              {axAgentValues.customWebsites &&
                axAgentValues.customWebsites.map((website, index) =>
                  editingIndex === index ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <Grid key={index} container alignItems="center" spacing={1}>
                      <Grid item xs>
                        <TextField
                          variant="outlined"
                          fullWidth
                          value={newWebsite}
                          onChange={e => setNewWebsite(e.target.value)}
                          autoFocus
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            handleAddWebsite();
                            setEditingIndex(null);
                          }}
                        >
                          Save
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditingIndex(null);
                            setNewWebsite('');
                          }}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid
                      container
                      alignItems="center"
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      className={
                        index % 2 === 0
                          ? classes.customWebsiteEvenRow
                          : classes.customWebsiteOddRow
                      }
                    >
                      <Typography variant="caption" color="textSecondary">
                        <FormattedMessage {...messages.websiteDomain} />
                      </Typography>
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justify="space-between"
                        className={classes.customWebsiteLabel}
                      >
                        <Typography variant="body2">{website}</Typography>
                        <Grid className={classes.actionButtons}>
                          <IconButton
                            onClick={() => {
                              setEditingIndex(index);
                              setNewWebsite(website);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteWebsite(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  ),
                )}
              {showAddWebsite && (
                <Grid
                  item
                  container
                  direction="column"
                  className={classes.addWebsite}
                >
                  <Typography variant="caption" color="textSecondary">
                    <FormattedMessage {...messages.websiteDomain} />
                  </Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={newWebsite}
                    onChange={e => setNewWebsite(e.target.value)}
                    placeholder="www.example.com"
                    className={classes.addWebsiteInput}
                  />
                  <Grid
                    container
                    direction="row"
                    justify="flex-end"
                    spacing={1}
                  >
                    <Grid item>
                      <Button
                        onClick={toggleAddWebsite}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className={classes.cancelBtn}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        onClick={handleAddWebsite}
                        variant="contained"
                        color="primary"
                        size="medium"
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {!showAddWebsite && (
                <Button
                  onClick={toggleAddWebsite}
                  startIcon={<AddIcon />}
                  variant="text"
                  color="primary"
                >
                  Add Website
                </Button>
              )}
            </Grid>
          )}

          {errors.visibility && (
            <FormHelperText error>{errors.visibility}</FormHelperText>
          )}
        </Grid>
      </Grid>
      <Grid className={classes.content}>
        <Grid className={classes.sectionHeader}>
          <Typography variant="h3">
            <FormattedMessage {...messages.defaultLanguage} />
          </Typography>
          <Typography variant="body2">
            <FormattedMessage {...messages.defaultLanguageDesc} />
          </Typography>
        </Grid>
        <Grid className={classes.contactInfo}>
          <Grid className={classes.formInput}>
            <StyledReactSelect
              options={languages}
              value={languages.find(
                lang => lang.key === axAgentValues.languageCode,
              )}
              onChange={handleLanguageChange}
              placeholder={intl.formatMessage(messages.selectDefaultLanguage)}
              getOptionLabel={(language: { label: string }) => language.label}
              getOptionValue={(language: { key: string }) => language.key}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid className={classes.content}>
        <Grid className={classes.sectionHeader}>
          <Typography variant="h3">
            <FormattedMessage {...messages.fallbackInteractions} />
          </Typography>
          <Typography variant="body2">
            <FormattedMessage {...messages.fallbackInteractionsInstruction} />
          </Typography>
        </Grid>
        <Grid className={classes.contactInfo}>
          <Grid className={classes.formInput}>
            <Typography variant="body2">
              <FormattedMessage {...messages.fallbackMethod} />
            </Typography>
            <Select
              value={fallbackMethod}
              onChange={handleFallbackMethodChange}
              variant="outlined"
              fullWidth
              classes={{
                root: classes.root,
              }}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
            >
              <MenuItem value="Email" className={classes.menuItem}>
                Email
              </MenuItem>
              <MenuItem value="PhoneNumber" className={classes.menuItem}>
                PhoneNumber
              </MenuItem>
              <MenuItem value="Website" className={classes.menuItem}>
                Website
              </MenuItem>
            </Select>
          </Grid>
          {fallbackMethod === 'Email' && (
            <Grid className={classes.formInput}>
              <Typography variant="body2">
                <FormattedMessage {...messages.email} />
              </Typography>
              <TextField
                placeholder="email@domain.com"
                fullWidth
                value={axAgentValues.emailFallback}
                variant="outlined"
                size="small"
                name="emailFallback"
                onChange={handleChange}
              />
            </Grid>
          )}
          {fallbackMethod === 'PhoneNumber' && (
            <Grid className={classes.formInput}>
              <Typography variant="body2">
                <FormattedMessage {...messages.phoneNumber} />
              </Typography>
              <TextField
                placeholder="(123) 456-7890"
                fullWidth
                value={axAgentValues.phoneNumberFallback}
                variant="outlined"
                size="small"
                name="phoneNumberFallback"
                onChange={handleChange}
              />
            </Grid>
          )}
          {fallbackMethod === 'Website' && (
            <Grid className={classes.formInput}>
              <Typography variant="body2">
                <FormattedMessage {...messages.website} />
              </Typography>
              <TextField
                placeholder="www.website.com/faq"
                fullWidth
                value={axAgentValues.websiteFallback}
                variant="outlined"
                size="small"
                name="websiteFallback"
                onChange={handleChange}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid className={classes.content}>
        <Grid className={classes.sectionHeader}>
          <Typography variant="h3">
            <FormattedMessage {...messages.jobsDirectoryCareerSiteUrl} />
          </Typography>
          <Typography variant="body2">
            <FormattedMessage
              {...messages.jobsDirectoryCareerSiteUrlInstruction}
            />
          </Typography>
        </Grid>
        <Grid className={classes.contactInfo}>
          <Grid className={classes.formInput}>
            <Typography variant="body2">
              <FormattedMessage {...messages.websiteDomain} />
            </Typography>
            <TextField
              placeholder="www.careersite.com"
              value={axAgentValues.careerSiteUrl}
              fullWidth
              variant="outlined"
              size="small"
              name="careerSiteUrl"
              error={!!errors.careerSiteUrl}
              onChange={handleChange}
            />
            {errors.careerSiteUrl && (
              <FormHelperText error>{errors.careerSiteUrl}</FormHelperText>
            )}
          </Grid>
        </Grid>
      </Grid>

      <DeleteWebsiteConfirmationModal
        handleClose={() => setOpenConfirmDialog(false)}
        openConfirmDialog={openConfirmDialog}
        deleteIndex={deleteIndex}
        setOpenConfirmDialog={setOpenConfirmDialog}
        setAxAgentValues={setAxAgentValues}
      />
      <EmbedCodeModal
        open={openEmbedCodeModal}
        handleClose={handleCloseEmbedModal}
        agentId={axAgentValues.axAgentId}
      />
    </Grid>
  );
};
